import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Missing reset token — request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset. Redirecting to login…");
      setTimeout(() => navigate("/login"), 1400);
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-md px-6 py-16">
        <div className="border border-border bg-card" data-testid="reset-password-card">
          <div className="border-b border-border px-6 py-4 flex items-center justify-between">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">// auth · reset</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
              {done ? "complete" : "new key"}
            </div>
          </div>

          {done ? (
            <div className="p-6 space-y-4" data-testid="reset-password-done">
              <h1 className="font-display font-bold text-3xl">All set.</h1>
              <p className="text-sm text-muted-foreground font-mono">
                {">"} your password has been updated. redirecting to login…
              </p>
              <Link to="/login" data-testid="reset-password-login-link">
                <Button className="w-full rounded-none font-mono uppercase tracking-widest">
                  Continue to login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-5" data-testid="reset-password-form">
              <div>
                <h1 className="font-display font-bold text-3xl">Choose a new password.</h1>
                <p className="mt-2 text-sm text-muted-foreground font-mono">
                  {">"} pick something at least 6 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-widest" htmlFor="password">new_password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-none font-mono"
                  placeholder="••••••••"
                  data-testid="reset-password-new-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-widest" htmlFor="confirm">confirm</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-none font-mono"
                  placeholder="••••••••"
                  data-testid="reset-password-confirm-input"
                />
              </div>

              {err && (
                <div className="border border-destructive/40 bg-destructive/10 text-destructive font-mono text-xs px-3 py-2" data-testid="reset-password-error">
                  ! {err}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy || !token}
                className="w-full rounded-none font-mono uppercase tracking-widest h-11"
                data-testid="reset-password-submit-button"
              >
                {busy ? "Updating…" : "Set new password"}
              </Button>

              <div className="font-mono text-xs text-muted-foreground text-center">
                {">"} link expired?{" "}
                <Link to="/forgot-password" className="text-primary underline-offset-4 hover:underline" data-testid="reset-password-request-new-link">
                  request a new one
                </Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
